import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, LogOut, ExternalLink, MessagesSquare, ShieldCheck, MonitorSmartphone, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";

  const roleLabel = {
    super_admin: "Super Admin",
    admin: "Admin",
    user: "User",
    read_only: "Read Only",
  }[user?.role] || user?.role || "";

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
      isActive ? "bg-brandblue text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-lightbg flex flex-col md:flex-row" data-testid="admin-layout">
      <aside className="md:w-64 bg-navy text-white md:min-h-screen flex md:flex-col shrink-0">
        <div className="p-5 flex items-center gap-3 border-b border-white/10 flex-1 md:flex-none">
          <Logo variant="white" />
          <span className="text-[10px] tracking-[0.2em] text-cyan font-semibold border-l border-white/15 pl-3">ADMIN</span>
        </div>
        <nav className="p-4 flex md:flex-col gap-2 md:flex-1">
          <NavLink to="/admin" end className={linkCls} data-testid="admin-nav-leads">
            <LayoutDashboard size={18} /> <span className="hidden md:inline">Leads</span>
          </NavLink>
          {isAdmin && (
            <NavLink to="/admin/content" className={linkCls} data-testid="admin-nav-content">
              <Package size={18} />
              <span className="hidden md:inline">Packages & Services</span>
            </NavLink>
          )}
          <NavLink to="/admin/chats" className={linkCls} data-testid="admin-nav-chats">
            <MessagesSquare size={18} /> <span className="hidden md:inline">Conversations</span>
          </NavLink>
          <div className="mt-2">
            <div className="hidden md:flex items-center gap-2 px-4 py-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-white/35">
              <ShieldCheck size={14} />
              Security
            </div>

            <div className="md:pl-3 space-y-1">
              <NavLink
                to="/admin/security"
                end
                className={linkCls}
                data-testid="admin-nav-security"
              >
                <ShieldCheck size={18} />
                <span className="hidden md:inline">Account Security</span>
              </NavLink>

              {isAdmin && (
                <NavLink
                  to="/admin/security/logs"
                  className={linkCls}
                  data-testid="admin-nav-security-logs"
                >
                  <ShieldCheck size={18} />
                  <span className="hidden md:inline">Security Logs</span>
                </NavLink>
              )}

              <NavLink
                to="/admin/security/sessions"
                className={linkCls}
                data-testid="admin-nav-security-sessions"
              >
                <MonitorSmartphone size={18} />
                <span className="hidden md:inline">Active Sessions</span>
              </NavLink>

              {isSuperAdmin && (
                <NavLink
                  to="/admin/security/users"
                  className={linkCls}
                  data-testid="admin-nav-security-users"
                >
                  <Users size={18} />
                  <span className="hidden md:inline">Users</span>
                </NavLink>
              )}
            </div>
          </div>
        </nav>
        <div className="p-4 border-t border-white/10 hidden md:block">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/60 hover:text-cyan transition-colors">
            <ExternalLink size={16} /> View site
          </a>
          <div className="px-4 py-2">
            <div className="text-xs text-white/50 truncate">{user?.email}</div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-cyan">
              {roleLabel}
            </div>
          </div>
          <button onClick={doLogout} data-testid="admin-logout-btn" className="mt-1 flex items-center gap-3 px-4 py-2.5 text-sm text-white/70 hover:text-white w-full transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-5 sm:p-8 lg:p-10 overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
